import { useEffect, useRef, type ReactNode } from 'react';
import * as stylex from '@stylexjs/stylex';
import { borderRadius, color, spacer } from '@figma/fpl-tokens/tokens.stylex';
import { ColorTheme } from '@figma/fpl-themes';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ButtonPrimitive, IconButton, ModalPrimitive } from '@figma/fpl-components';
import { Icon24Close, Icon24Copy, Icon24Export, Icon24Image, Icon24Trash } from '@figma/fpl-icons';
import { useThemeMode } from '@/contexts/ThemeModeContext';
import { ZIndex } from '@/consts/z-indexes.consts';
import { GalleryFileViewer } from '@/components/Common/ImageList/GalleryFileViewer';
import LazyThreeDeeViewer from '@/components/Nodes/LazyThreeDeeViewer';
import { ParamsPanel } from '@/components/Recipe/FlowComponents/MediaGallery/ParamsPanel';
import { I18N_KEYS } from '@/language/keys';
import { useResolvedAssets } from '@/state/workflow.state';
import { assetUrl } from '@/utils/assetUrls';
import { Tooltip } from '@/UI/Tooltip/Tooltip';
import { useToolOutputAssetActions } from './useToolOutputAssetActions';
import { isPreviewableOutput } from './ToolOutputDownload';
import { ToolOutputThumbnail } from './ToolOutputThumbnail';
import type { MediaAsset, TextAsset } from '@/types/api/assets';
import type { ToolOutputActionsProps } from './ToolOutputActions';

type ToolOutputPreviewAsset = (MediaAsset | TextAsset) & {
  name?: string;
  runId?: string;
};

export type ToolOutputPreviewSelection = Pick<ToolOutputPreviewAsset, 'id' | 'runId'>;

interface ToolOutputPreviewProps {
  selection: ToolOutputPreviewSelection;
  results: ToolOutputPreviewAsset[];
  workflowName: string;
  removal?: {
    onRemove: NonNullable<ToolOutputActionsProps['onRemove']>;
    disabled: boolean;
  };
  onSelect: (selection: ToolOutputPreviewSelection) => void;
  onClose: () => void;
}

export function ToolOutputPreview({
  selection,
  results: allResults,
  workflowName,
  removal,
  onSelect,
  onClose,
}: ToolOutputPreviewProps) {
  const { t } = useTranslation();
  const resolvedAssets = useResolvedAssets();
  const thumbnailsSidebarRef = useRef<HTMLElement>(null);
  const thumbnailRef = useRef<HTMLButtonElement>(null);
  const results = allResults.filter((result) => isPreviewableOutput(result));
  const selectedIndex = results.findIndex((result) => result.id === selection.id && result.runId === selection.runId);
  const index = Math.max(0, selectedIndex);
  const result = results[index];
  useEffect(() => {
    if (!result) {
      onClose();
    } else if (selectedIndex === -1) {
      onSelect({ id: result.id, runId: result.runId });
    }
  }, [result, selectedIndex, onClose, onSelect]);
  useEffect(() => {
    thumbnailRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [result]);
  if (!result) {
    return null;
  }

  const select = (next: number) => {
    const output = results[next];
    if (output) {
      onSelect({ id: output.id, runId: output.runId });
    }
  };
  const name = result.name || t(I18N_KEYS.GENERAL.OUTPUT);
  const url = assetUrl(result, resolvedAssets);
  const key = JSON.stringify([result.runId ?? null, result.id]);
  let viewer: ReactNode;
  if (result.type !== 'text' && !url) {
    viewer = <Icon24Image />;
  } else if (result.type === '3D' && url) {
    viewer = <LazyThreeDeeViewer objUrl={url} mime={result.mime} responsive allowInteraction displayBackground />;
  } else {
    viewer = <GalleryFileViewer container="design-app" presentation="preview" file={result} index={0} selected={0} />;
  }
  return (
    <ToolOutputPreviewPortal>
      <ModalPrimitive.Root
        onClose={onClose}
        styles={{
          root: stylex.props(styles.root(ZIndex.Overlays.CONFIRMATION_DIALOGS)),
          contents: stylex.props(styles.layout),
          backdrop: stylex.props(styles.backdrop),
        }}
        onKeyDown={(event) => {
          if (!(event.target instanceof HTMLElement)) {
            return;
          }
          const isInThumbnailsSidebar = thumbnailsSidebarRef.current?.contains(event.target);
          if (
            !isInThumbnailsSidebar &&
            event.target.closest('input,textarea,select,audio,video,[contenteditable],canvas')
          ) {
            return;
          }
          let nextIndex: number;
          switch (event.key) {
            case 'ArrowRight':
            case 'ArrowDown':
              nextIndex = Math.min(index + 1, results.length - 1);
              break;
            case 'ArrowLeft':
            case 'ArrowUp':
              nextIndex = Math.max(index - 1, 0);
              break;
            case 'Home':
              nextIndex = 0;
              break;
            case 'End':
              nextIndex = results.length - 1;
              break;
            default:
              return;
          }
          event.preventDefault();
          if (isInThumbnailsSidebar) {
            thumbnailsSidebarRef.current?.querySelectorAll('button')[nextIndex]?.focus();
          }
          select(nextIndex);
        }}
      >
        <nav
          ref={thumbnailsSidebarRef}
          {...stylex.props(styles.thumbnailsSidebar)}
          aria-label={t(I18N_KEYS.GENERAL.OUTPUT)}
        >
          {results.map((output, outputIndex) => (
            <ButtonPrimitive
              key={JSON.stringify([output.runId ?? null, output.id])}
              ref={outputIndex === index ? thumbnailRef : undefined}
              type="button"
              aria-label={output.name || t(I18N_KEYS.GENERAL.OUTPUT)}
              aria-pressed={outputIndex === index}
              {...stylex.props(styles.thumbnail, outputIndex === index && styles.selectedThumbnail)}
              onClick={() => select(outputIndex)}
            >
              <ToolOutputThumbnail asset={output} selected={outputIndex === index} />
            </ButtonPrimitive>
          ))}
        </nav>
        <div {...stylex.props(styles.center, result.type === 'image' && styles.imageCenter)}>
          <div {...stylex.props(styles.viewer)} key={key}>
            {viewer}
          </div>
        </div>
        <aside {...stylex.props(styles.details)}>
          <PreviewActions result={result} workflowName={workflowName} removal={removal} onClose={onClose} />
          <ModalPrimitive.HiddenTitle>{name}</ModalPrimitive.HiddenTitle>
          <div {...stylex.props(styles.params)}>
            <ParamsPanel selectedFile={result} presentation="embedded" />
          </div>
        </aside>
      </ModalPrimitive.Root>
    </ToolOutputPreviewPortal>
  );
}

interface PreviewActionsProps extends Pick<ToolOutputPreviewProps, 'workflowName' | 'removal' | 'onClose'> {
  result: ToolOutputPreviewAsset;
}

function PreviewActions({ result, workflowName, removal, onClose }: PreviewActionsProps) {
  const { t } = useTranslation();
  const labels = I18N_KEYS.SHARED_DESIGN_APP.OUTPUT_HISTORY;
  const name = result.name || t(I18N_KEYS.GENERAL.OUTPUT);
  const { link, canDownload, download, copyLink } = useToolOutputAssetActions({
    result,
    workflowName,
    name,
  });
  return (
    <div {...stylex.props(styles.actions)}>
      <IconButton size="lg" aria-label={t(labels.DOWNLOAD, { name })} disabled={!canDownload} onClick={download}>
        <Icon24Export />
      </IconButton>
      {result.type !== 'text' && (
        <Tooltip title={t(I18N_KEYS.GENERAL.COPY_LINK)}>
          <IconButton size="lg" aria-label={t(I18N_KEYS.GENERAL.COPY_LINK)} disabled={!link} onClick={copyLink}>
            <Icon24Copy />
          </IconButton>
        </Tooltip>
      )}
      {removal && (
        <IconButton
          size="lg"
          aria-label={t(labels.REMOVE)}
          disabled={removal.disabled}
          onClick={() => removal.onRemove({ id: result.id, runId: result.runId })}
        >
          <Icon24Trash />
        </IconButton>
      )}
      <div {...stylex.props(styles.closeAction)}>
        <IconButton aria-label={t(I18N_KEYS.GENERAL.CLOSE)} autoFocus onClick={onClose}>
          <Icon24Close />
        </IconButton>
      </div>
    </div>
  );
}

function ToolOutputPreviewPortal({ children }: { children: ReactNode }) {
  const themeMode = useThemeMode();
  return createPortal(
    <ColorTheme brand="weave" mode={themeMode}>
      {children}
    </ColorTheme>,
    document.body,
  );
}

const styles = stylex.create({
  root: (zIndex: number) => ({ position: 'fixed', inset: 0, zIndex, color: color.text }),
  backdrop: { position: 'absolute', inset: 0, backgroundColor: 'rgb(0 0 0 / 60%)', backdropFilter: 'blur(20.4px)' },
  layout: {
    position: 'relative',
    display: 'grid',
    height: '100%',
    gridTemplateColumns: '108px minmax(0, 1fr) 270px',
  },
  thumbnailsSidebar: {
    width: 108,
    display: 'flex',
    flexDirection: 'column',
    gap: spacer.spacer8px,
    padding: spacer.spacer24px,
    overflowY: 'auto',
  },
  thumbnail: {
    position: 'relative',
    scrollMargin: 4,
    flexShrink: 0,
    width: 60,
    height: 60,
    padding: 0,
    borderWidth: 0,
    borderRadius: borderRadius.medium,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    color: color.icon,
  },
  selectedThumbnail: {
    '::after': {
      content: '""',
      position: 'absolute',
      inset: -4,
      boxSizing: 'border-box',
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: color.borderStrong,
      borderRadius: borderRadius.medium,
      pointerEvents: 'none',
    },
  },
  center: {
    minWidth: 0,
    minHeight: 0,
    padding: spacer.spacer24px,
  },
  imageCenter: { padding: 0 },
  viewer: {
    width: '100%',
    height: '100%',
    minWidth: 0,
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    padding: spacer.spacer16px,
    borderLeftWidth: 1,
    borderLeftStyle: 'solid',
    borderLeftColor: color.bordertranslucent,
    gap: spacer.spacer16px,
    backgroundColor: color.bg,
  },
  actions: { display: 'flex', alignItems: 'center' },
  closeAction: { marginLeft: 'auto' },
  params: { flex: 1, minHeight: 0, overflow: 'auto' },
});
